import { useEffect, useState, useMemo } from 'react';
import { fetchWrapper } from '@/_helpers';

export const useClientMedicationInformation = (form, watch, setValue) => {
    const [medicationInfo, setMedicationInfo] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const medicationFieldsMeta = useMemo(() => {
        try {
            if (!form || (form?.specialityForm || {})?.type !== 'clientMedications') {
                return {};
            }
            const elements = form.data || [];
            const findByKey = (key) =>
                elements.find((item) => {
                    const specialityKey = item?.specialityKey || (item?.config || {})?.specialityKey;
                    return specialityKey === key;
                });

            const medicationField = findByKey('medication');
            const infoField = findByKey('medicationInfo');

            const medicationFieldName = medicationField
                ? medicationField.name || `Select-${medicationField._id}`
                : null;
            const infoFieldName = infoField ? infoField.name || `medicationInfo-${infoField._id}` : null;

            return {
                medicationField,
                medicationFieldName,
                infoFieldName,
            };
        } catch (error) {
            console.error('Error processing medication fields meta:', error);
            return {};
        }
    }, [form]);

    const medicationValue = medicationFieldsMeta.medicationFieldName
        ? watch(medicationFieldsMeta.medicationFieldName)
        : undefined;

    useEffect(() => {
        let ignore = false;

        try {
            if (!form || form?.specialityForm?.type !== 'clientMedications') return;
            if (!medicationFieldsMeta.medicationFieldName) return;

            const infoFieldName = medicationFieldsMeta.infoFieldName;

            const fetchMedicationInfo = async (medValue) => {
                try {
                    if (!medValue) {
                        if (!ignore) {
                            setMedicationInfo('');
                            if (infoFieldName) {
                                setValue(infoFieldName, '');
                            }
                        }
                        return;
                    }

                    const medField = medicationFieldsMeta.medicationField;
                    let medName = medValue;
                    let prescribedText = '';

                    if (medField?.data?.length) {
                        const option = medField.data.find((opt) => opt.value === medValue);
                        if (option) {
                            medName = option.text || medValue;
                            const parts = [];
                            if (option.dosage) parts.push(`Prescribed dosage: ${option.dosage}`);
                            if (option.frequency) parts.push(`Prescribed frequency: ${option.frequency}`);
                            prescribedText = parts.join('\n');
                        }
                    }

                    if (!ignore) {
                        setMedicationInfo(
                            prescribedText
                                ? `${prescribedText}\n\nLoading FDA information...`
                                : 'Loading medication information...'
                        );
                        setIsLoading(true);
                    }

                    try {
                        const response = await fetchWrapper.get(`medications/info?name=${encodeURIComponent(medName)}`);
                        if (ignore) return;

                        const final = [];
                        if (prescribedText) final.push(prescribedText);
                        final.push(response?.summary || 'No FDA information available.');

                        const info = final.join('\n\n');
                        setMedicationInfo(info);
                        if (infoFieldName) {
                            setValue(infoFieldName, info);
                        }
                    } catch (fetchError) {
                        if (ignore) return;
                        const errorInfo = prescribedText
                            ? `${prescribedText}\n\nFDA information unavailable.`
                            : 'Unable to fetch medication information.';
                        setMedicationInfo(errorInfo);
                        if (infoFieldName) {
                            setValue(infoFieldName, errorInfo);
                        }
                        console.error('Error fetching medication info:', fetchError);
                    } finally {
                        if (!ignore) {
                            setIsLoading(false);
                        }
                    }
                } catch (error) {
                    console.error('Unexpected error in fetchMedicationInfo:', error);
                    if (!ignore) {
                        setIsLoading(false);
                    }
                }
            };

            fetchMedicationInfo(medicationValue);
        } catch (error) {
            console.error('Unexpected error in medication info effect:', error);
            if (!ignore) {
                setIsLoading(false);
            }
        }

        return () => {
            ignore = true;
        };
    }, [form, medicationValue, medicationFieldsMeta, setValue]);

    return { medicationInfo, isLoading };
};
